// painel-admin/src/pages/TikpagExperience/TikpagExperienceCampanhas.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import {
  CalendarDays,
  Edit3,
  Plus,
  RefreshCcw,
  Search,
  Sparkles,
  Trash2,
  X
} from "lucide-react";

import axios from "../../axiosConfig";
import "./TikpagExperienceCampanhas.css";

const FORM_INICIAL = {
  id: null,
  nome: "",
  slug: "",
  descricao: "",
  regulamento: "",
  imagem_banner: "",
  data_inicio: "",
  data_fim: "",
  periodicidade: "MENSAL",
  exige_inscricao: true,
  contabilizar_antes_inscricao: false,
  ativa: true,
  status: "RASCUNHO"
};

const formatarDataInput = (valor) => {
  if (!valor) return "";

  return String(valor).slice(0, 10);
};

const formatarData = (valor) => {
  if (!valor) return "-";

  return new Date(valor).toLocaleDateString(
    "pt-BR",
    {
      timeZone: "UTC"
    }
  );
};

const classeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

export default function TikpagExperienceCampanhas() {
  const [campanhas, setCampanhas] =
    useState([]);

  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] =
    useState("");

  const [formulario, setFormulario] =
    useState(FORM_INICIAL);

  const [modalAberto, setModalAberto] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [salvando, setSalvando] =
    useState(false);

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] =
    useState("");

  const carregarCampanhas =
    useCallback(async () => {
      setLoading(true);
      setErro("");

      try {
        const resposta = await axios.get(
          "/admin/experience/campanhas"
        );

        setCampanhas(
          Array.isArray(
            resposta.data?.campanhas
          )
            ? resposta.data.campanhas
            : []
        );
      } catch (err) {
        console.error(
          "Erro ao carregar campanhas Experience:",
          err
        );

        setErro(
          err.response?.data?.error ||
            "Não foi possível carregar as campanhas."
        );
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    carregarCampanhas();
  }, [carregarCampanhas]);

  const campanhasFiltradas =
    useMemo(() => {
      const termo =
        busca.trim().toLowerCase();

      return campanhas.filter(
        (campanha) => {
          const correspondeBusca =
            !termo ||
            [
              campanha.nome,
              campanha.slug,
              campanha.descricao,
              campanha.status
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(termo);

          const correspondeStatus =
            !statusFiltro ||
            campanha.status ===
              statusFiltro;

          return (
            correspondeBusca &&
            correspondeStatus
          );
        }
      );
    }, [
      campanhas,
      busca,
      statusFiltro
    ]);

  const abrirNovaCampanha = () => {
    setFormulario(FORM_INICIAL);
    setErro("");
    setSucesso("");
    setModalAberto(true);
  };

  const abrirEdicao = (campanha) => {
    setFormulario({
      id: Number(campanha.id),
      nome: campanha.nome || "",
      slug: campanha.slug || "",
      descricao:
        campanha.descricao || "",
      regulamento:
        campanha.regulamento || "",
      imagem_banner:
        campanha.imagem_banner || "",
      data_inicio:
        formatarDataInput(
          campanha.data_inicio
        ),
      data_fim:
        formatarDataInput(
          campanha.data_fim
        ),
      periodicidade:
        campanha.periodicidade ||
        "MENSAL",
      exige_inscricao:
        Boolean(
          campanha.exige_inscricao
        ),
      contabilizar_antes_inscricao:
        Boolean(
          campanha
            .contabilizar_antes_inscricao
        ),
      ativa:
        Boolean(campanha.ativa),
      status:
        campanha.status ||
        "RASCUNHO"
    });

    setErro("");
    setSucesso("");
    setModalAberto(true);
  };

  const fecharModal = () => {
    if (salvando) return;

    setModalAberto(false);
    setFormulario(FORM_INICIAL);
  };

  const atualizarCampo = (
    campo,
    valor
  ) => {
    setFormulario((atual) => ({
      ...atual,
      [campo]: valor
    }));
  };

  const salvarCampanha = async (
    event
  ) => {
    event.preventDefault();

    if (!formulario.nome.trim()) {
      setErro(
        "Informe o nome da campanha."
      );
      return;
    }

    if (
      !formulario.data_inicio ||
      !formulario.data_fim
    ) {
      setErro(
        "Informe a data de início e a data final."
      );
      return;
    }

    if (
      formulario.data_fim <
      formulario.data_inicio
    ) {
      setErro(
        "A data final não pode ser anterior à data inicial."
      );
      return;
    }

    setSalvando(true);
    setErro("");
    setSucesso("");

    try {
      const payload = {
        nome:
          formulario.nome.trim(),

        slug:
          formulario.slug.trim(),

        descricao:
          formulario.descricao.trim(),

        regulamento:
          formulario.regulamento.trim(),

        imagem_banner:
          formulario.imagem_banner.trim(),

        data_inicio:
          formulario.data_inicio,

        data_fim:
          formulario.data_fim,

        periodicidade:
          formulario.periodicidade,

        exige_inscricao:
          formulario.exige_inscricao,

        contabilizar_antes_inscricao:
          formulario
            .contabilizar_antes_inscricao,

        ativa:
          formulario.ativa,

        status:
          formulario.status
      };

      if (formulario.id) {
        await axios.put(
          `/admin/experience/campanhas/${formulario.id}`,
          payload
        );

        setSucesso(
          "Campanha atualizada com sucesso."
        );
      } else {
        await axios.post(
          "/admin/experience/campanhas",
          payload
        );

        setSucesso(
          "Campanha criada com sucesso."
        );
      }

      setModalAberto(false);
      setFormulario(FORM_INICIAL);

      await carregarCampanhas();
    } catch (err) {
      console.error(
        "Erro ao salvar campanha Experience:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível salvar a campanha."
      );
    } finally {
      setSalvando(false);
    }
  };

  const excluirCampanha = async (
    campanha
  ) => {
    const confirmou =
      window.confirm(
        `Excluir a campanha "${campanha.nome}"?`
      );

    if (!confirmou) return;

    setErro("");
    setSucesso("");

    try {
      await axios.delete(
        `/admin/experience/campanhas/${campanha.id}`
      );

      setSucesso(
        "Campanha excluída com sucesso."
      );

      await carregarCampanhas();
    } catch (err) {
      console.error(
        "Erro ao excluir campanha Experience:",
        err
      );

      setErro(
        err.response?.data?.error ||
          "Não foi possível excluir a campanha."
      );
    }
  };

  return (
    <div className="experienceCampanhas-page">
      <header className="experienceCampanhas-header">
        <div>
          <span className="experienceCampanhas-kicker">
            Tikpag Experience
          </span>

          <h1>Campanhas</h1>

          <p>
            Gerencie vigência, inscrições,
            regulamento e status das campanhas
            Experience.
          </p>
        </div>

        <button
          type="button"
          className="experienceCampanhas-primaryButton"
          onClick={abrirNovaCampanha}
        >
          <Plus size={16} />
          Nova campanha
        </button>
      </header>

      {erro && (
        <div className="experienceCampanhas-alert erro">
          {erro}
        </div>
      )}

      {sucesso && (
        <div className="experienceCampanhas-alert sucesso">
          {sucesso}
        </div>
      )}

      <section className="experienceCampanhas-summary">
        <article className="experienceCampanhas-summaryCard">
          <div className="experienceCampanhas-summaryIcon">
            <Sparkles size={20} />
          </div>

          <div>
            <span>
              Campanhas cadastradas
            </span>

            <strong>
              {campanhas.length}
            </strong>
          </div>
        </article>

        <article className="experienceCampanhas-summaryCard">
          <div className="experienceCampanhas-summaryIcon">
            <CalendarDays size={20} />
          </div>

          <div>
            <span>
              Em andamento
            </span>

            <strong>
              {
                campanhas.filter(
                  (campanha) =>
                    campanha.status ===
                    "EM_ANDAMENTO"
                ).length
              }
            </strong>
          </div>
        </article>

        <article className="experienceCampanhas-summaryCard">
          <div className="experienceCampanhas-summaryIcon">
            <Sparkles size={20} />
          </div>

          <div>
            <span>
              Em inscrições
            </span>

            <strong>
              {
                campanhas.filter(
                  (campanha) =>
                    campanha.status ===
                    "INSCRICOES"
                ).length
              }
            </strong>
          </div>
        </article>
      </section>

      <section className="experienceCampanhas-panel">
        <div className="experienceCampanhas-toolbar">
          <div className="experienceCampanhas-search">
            <Search size={16} />

            <input
              value={busca}
              onChange={(event) =>
                setBusca(
                  event.target.value
                )
              }
              placeholder="Buscar campanha, slug ou status"
            />
          </div>

          <div className="experienceCampanhas-filters">
            <select
              value={statusFiltro}
              onChange={(event) =>
                setStatusFiltro(
                  event.target.value
                )
              }
            >
              <option value="">
                Todos os status
              </option>

              <option value="RASCUNHO">
                Rascunho
              </option>

              <option value="INSCRICOES">
                Inscrições
              </option>

              <option value="EM_ANDAMENTO">
                Em andamento
              </option>

              <option value="ENCERRADA">
                Encerrada
              </option>

              <option value="CANCELADA">
                Cancelada
              </option>
            </select>

            <button
              type="button"
              className="experienceCampanhas-secondaryButton"
              onClick={
                carregarCampanhas
              }
              disabled={loading}
            >
              <RefreshCcw
                size={16}
                className={
                  loading
                    ? "experienceCampanhas-spin"
                    : ""
                }
              />

              Atualizar
            </button>
          </div>
        </div>

        <div className="experienceCampanhas-list">
          {campanhasFiltradas.map(
            (campanha) => (
              <article
                className="experienceCampanhas-card"
                key={campanha.id}
              >
                <div className="experienceCampanhas-main">
                  <div className="experienceCampanhas-identity">
                    <div className="experienceCampanhas-icon">
                      {campanha.imagem_banner ? (
                        <img
                          src={
                            campanha.imagem_banner
                          }
                          alt=""
                        />
                      ) : (
                        <Sparkles
                          size={21}
                        />
                      )}
                    </div>

                    <div>
                      <h2>
                        {campanha.nome}
                      </h2>

                      <p>
                        {campanha.slug}
                      </p>

                      <div className="experienceCampanhas-statusRow">
                        <span
                          className={`experienceCampanhas-status ${classeStatus(
                            campanha.status
                          )}`}
                        >
                          {campanha.status}
                        </span>

                        <span
                          className={`experienceCampanhas-status ${
                            campanha.ativa
                              ? "ativa"
                              : "inativa"
                          }`}
                        >
                          {campanha.ativa
                            ? "Ativa"
                            : "Inativa"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="experienceCampanhas-metric">
                    <span>Vigência</span>

                    <strong>
                      {formatarData(
                        campanha.data_inicio
                      )}
                      {" até "}
                      {formatarData(
                        campanha.data_fim
                      )}
                    </strong>
                  </div>

                  <div className="experienceCampanhas-metric">
                    <span>PINs</span>

                    <strong>
                      {Number(
                        campanha.quantidade_pins ||
                          0
                      )}
                    </strong>
                  </div>

                  <div className="experienceCampanhas-metric">
                    <span>Inscritos</span>

                    <strong>
                      {Number(
                        campanha.quantidade_inscritos ||
                          0
                      )}
                    </strong>
                  </div>

                  <div className="experienceCampanhas-actions">
                    <button
                      type="button"
                      className="experienceCampanhas-editButton"
                      onClick={() =>
                        abrirEdicao(
                          campanha
                        )
                      }
                    >
                      <Edit3 size={15} />
                      Editar
                    </button>

                    <button
                      type="button"
                      className="experienceCampanhas-deleteButton"
                      onClick={() =>
                        excluirCampanha(
                          campanha
                        )
                      }
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </article>
            )
          )}

          {!loading &&
            !campanhasFiltradas.length && (
              <div className="experienceCampanhas-empty">
                Nenhuma campanha encontrada.
              </div>
            )}

          {loading && (
            <div className="experienceCampanhas-empty">
              Carregando campanhas...
            </div>
          )}
        </div>
      </section>

      {modalAberto && (
        <div
          className="experienceCampanhas-modalOverlay"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              fecharModal();
            }
          }}
        >
          <form
            className="experienceCampanhas-modal"
            onSubmit={salvarCampanha}
          >
            <div className="experienceCampanhas-modalHeader">
              <div>
                <span className="experienceCampanhas-kicker">
                  Tikpag Experience
                </span>

                <h2>
                  {formulario.id
                    ? "Editar campanha"
                    : "Nova campanha"}
                </h2>
              </div>

              <button
                type="button"
                className="experienceCampanhas-closeButton"
                onClick={fecharModal}
              >
                <X size={19} />
              </button>
            </div>

            <div className="experienceCampanhas-modalBody">
              <div className="experienceCampanhas-formGrid">
                <label className="experienceCampanhas-field">
                  <span>
                    Nome da campanha
                  </span>

                  <input
                    value={
                      formulario.nome
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "nome",
                        event.target.value
                      )
                    }
                    placeholder="Ex.: Tikpag Experience"
                    autoFocus
                  />
                </label>

                <label className="experienceCampanhas-field">
                  <span>Slug</span>

                  <input
                    value={
                      formulario.slug
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "slug",
                        event.target.value
                      )
                    }
                    placeholder="tikpag-experience"
                  />
                </label>

                <label className="experienceCampanhas-field">
                  <span>
                    Data de início
                  </span>

                  <input
                    type="date"
                    value={
                      formulario.data_inicio
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "data_inicio",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experienceCampanhas-field">
                  <span>
                    Data final
                  </span>

                  <input
                    type="date"
                    value={
                      formulario.data_fim
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "data_fim",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experienceCampanhas-field">
                  <span>
                    Periodicidade
                  </span>

                  <select
                    value={
                      formulario.periodicidade
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "periodicidade",
                        event.target.value
                      )
                    }
                  >
                    <option value="MENSAL">
                      Mensal
                    </option>

                    <option value="TRIMESTRAL">
                      Trimestral
                    </option>

                    <option value="SEMANAL">
                      Semanal
                    </option>
                  </select>
                </label>

                <label className="experienceCampanhas-field">
                  <span>Status</span>

                  <select
                    value={
                      formulario.status
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "status",
                        event.target.value
                      )
                    }
                  >
                    <option value="RASCUNHO">
                      Rascunho
                    </option>

                    <option value="INSCRICOES">
                      Inscrições
                    </option>

                    <option value="EM_ANDAMENTO">
                      Em andamento
                    </option>

                    <option value="ENCERRADA">
                      Encerrada
                    </option>

                    <option value="CANCELADA">
                      Cancelada
                    </option>
                  </select>
                </label>

                <label className="experienceCampanhas-field full">
                  <span>
                    URL do banner
                  </span>

                  <input
                    value={
                      formulario.imagem_banner
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "imagem_banner",
                        event.target.value
                      )
                    }
                    placeholder="https://..."
                  />
                </label>

                <label className="experienceCampanhas-field full">
                  <span>Descrição</span>

                  <textarea
                    value={
                      formulario.descricao
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "descricao",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experienceCampanhas-field full">
                  <span>
                    Regulamento
                  </span>

                  <textarea
                    value={
                      formulario.regulamento
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "regulamento",
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="experienceCampanhas-check">
                  <input
                    type="checkbox"
                    checked={
                      formulario.ativa
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "ativa",
                        event.target.checked
                      )
                    }
                  />

                  <span>
                    <strong>
                      Campanha ativa
                    </strong>

                    <small>
                      Permite uso normal da
                      campanha.
                    </small>
                  </span>
                </label>

                <label className="experienceCampanhas-check">
                  <input
                    type="checkbox"
                    checked={
                      formulario
                        .exige_inscricao
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "exige_inscricao",
                        event.target.checked
                      )
                    }
                  />

                  <span>
                    <strong>
                      Exige inscrição
                    </strong>

                    <small>
                      O cliente precisa aceitar
                      o regulamento.
                    </small>
                  </span>
                </label>

                <label className="experienceCampanhas-check full">
                  <input
                    type="checkbox"
                    checked={
                      formulario
                        .contabilizar_antes_inscricao
                    }
                    onChange={(event) =>
                      atualizarCampo(
                        "contabilizar_antes_inscricao",
                        event.target.checked
                      )
                    }
                  />

                  <span>
                    <strong>
                      Contabilizar operações
                      anteriores à inscrição
                    </strong>

                    <small>
                      Quando desmarcado, somente
                      operações posteriores à
                      inscrição serão apuradas.
                    </small>
                  </span>
                </label>
              </div>
            </div>

            <div className="experienceCampanhas-modalFooter">
              <button
                type="button"
                className="experienceCampanhas-secondaryButton"
                onClick={fecharModal}
                disabled={salvando}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="experienceCampanhas-primaryButton"
                disabled={salvando}
              >
                {salvando ? (
                  <RefreshCcw
                    size={16}
                    className="experienceCampanhas-spin"
                  />
                ) : (
                  <CalendarDays
                    size={16}
                  />
                )}

                {formulario.id
                  ? "Salvar alterações"
                  : "Criar campanha"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}